/**
 * Job health — `/v1/admin/jobs`, doc 45 §4.2.
 *
 * ⛔ **The server lists every REGISTERED job, not every job that has run.** A job with no rows
 * comes back with `last_run_at: null`, which means **never run** — and that is the single most
 * important thing this screen renders. Filtering those out client-side would undo the whole
 * point: a job that has never fired is exactly the one somebody needs to see.
 *
 * ⛔ **Nothing here fabricates a row, a status or a count.** This tab was deleted once for
 * rendering four invented runs, one of them a `settlement.reconcile` that "Succeeded with breaks"
 * for a job that did not exist — and an operator who reads that goes hunting for reconciliation
 * breaks that were never there.
 *
 * Polled rather than cached indefinitely: the question "is anything stuck?" has a different
 * answer every few minutes, and a stale panel answering it is worse than no panel.
 */
import { useQuery } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type JobHealthRow = components['schemas']['JobHealthRow'];
export type JobRunRow = components['schemas']['JobRunRow'];
type JobHealthList = components['schemas']['JobHealthResponse'];
type JobRunList = components['schemas']['JobRunListResponse'];
export type ErrorLogRow = components['schemas']['ErrorLogRow'];
type ErrorLogList = components['schemas']['ErrorLogListResponse'];

/** A minute: long enough not to hammer the API, short enough that a stuck job surfaces quickly. */
const REFETCH_MS = 60_000;

export function useJobHealth() {
  return useQuery({
    queryKey: keys.jobs.health(),
    queryFn: () => authedClient.request<JobHealthList>('/v1/admin/jobs'),
    refetchInterval: REFETCH_MS,
  });
}

/**
 * The newest captured 500s and failed jobs — `/v1/admin/errors`, arch-09 §10.
 *
 * Superadmin only on the server. `enabled` holds it to while its panel is open, so the audit
 * stream's own tab does not poll it every minute.
 */
export function useErrorTail(enabled: boolean) {
  return useQuery({
    queryKey: keys.jobs.errors(),
    queryFn: () => authedClient.request<ErrorLogList>('/v1/admin/errors'),
    refetchInterval: REFETCH_MS,
    enabled,
  });
}

export function useJobRuns(jobName: string | null) {
  /* ⛔ The path is narrowed HERE, not inside `queryFn`. Written the other way — a `?? ''` in the
     request itself — the fallback is unreachable, because `enabled` already guarantees the query
     never runs with a null name: a branch no test can take and no reader can justify. Narrowing
     first means both arms of this ternary are real (the hook is called with `null` on every
     render before a row is picked) and the request has no branch at all. */
  const path = jobName === null ? '' : `/v1/admin/jobs/${encodeURIComponent(jobName)}/runs`;
  return useQuery({
    queryKey: keys.jobs.runs(jobName ?? ''),
    queryFn: () => authedClient.request<JobRunList>(path),
    enabled: jobName !== null,
  });
}

/**
 * What one row means, as one word — the only place this judgement is made.
 *
 * ⛔ **`never` is its own verdict, not a flavour of healthy.** A job that has never run has not
 * passed anything; rendering it green, or as an empty cell, is the exact failure that got this
 * tab deleted. ⛔ **`running` is not success either** — it is a job that started and has not
 * reported back, which after a while is its own kind of bad news.
 */
export function jobVerdict(row: JobHealthRow): 'never' | 'ok' | 'failed' | 'skipped' | 'running' {
  if (row.last_run_at === null || row.last_run_at === undefined) return 'never';
  if (row.last_outcome === 'failure') return 'failed';
  if (row.last_outcome === 'skipped') return 'skipped';
  if (row.last_outcome === 'running') return 'running';
  return 'ok';
}
